import { useState, useRef, useEffect } from 'react';
import { MessageSquare, BookOpen, Lock, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { chatSend } from '../api';
import { useAuth } from '../context/AuthContext';

export default function Chat() {
  const { isPaid } = useAuth();
  const [msgs,setMsgs]   = useState([{from:'ai',text:"Hello! I am your AI legal research assistant. Ask me any legal question and I will reference relevant Cambodian law documents for you."}]);
  const [input,setInput] = useState('');
  const [busy,setBusy]   = useState(false);
  const sessionId        = useRef(Date.now().toString(36)+Math.random().toString(36).slice(2));
  const bottom           = useRef(null);

  useEffect(()=>{ bottom.current?.scrollIntoView({behavior:'smooth'}); },[msgs]);

  const send = async()=>{
    if(!input.trim()||busy) return;
    const q=input.trim(); setInput(''); setBusy(true);
    setMsgs(p=>[...p,{from:'user',text:q}]);
    try{
      const r=await chatSend(q,sessionId.current);
      setMsgs(p=>[...p,{from:'ai',text:r.data.reply}]);
    }catch{
      setMsgs(p=>[...p,{from:'ai',text:'Sorry, the AI assistant is temporarily unavailable. Please try again.'}]);
    }finally{ setBusy(false); }
  };

  return(
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-900 rounded-xl flex items-center justify-center"><MessageSquare size={18} className="text-white"/></div>
        <div><h1 className="text-xl font-bold text-blue-950">AI Legal Assistant</h1><p className="text-xs text-gray-400">Powered by Claude AI · References Cambodian law documents</p></div>
        {!isPaid()&&<span className="ml-auto text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1"><Lock size={10}/>Free Preview</span>}
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col" style={{height:440}}>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {msgs.map((m,i)=>(
            <div key={i} className={"flex items-start gap-2 "+(m.from==='user'?'justify-end':'justify-start')}>
              {m.from==='ai'&&<div className="w-7 h-7 bg-blue-900 rounded-lg flex items-center justify-center shrink-0 mt-1"><BookOpen size={13} className="text-white"/></div>}
              <div className={"max-w-xs md:max-w-md rounded-2xl px-4 py-3 text-sm leading-relaxed "+(m.from==='user'?'bg-blue-900 text-white':'bg-gray-100 text-gray-800')}>
                {m.text.split('\n').map((ln,j)=><p key={j} className={ln.startsWith('📄')?'font-semibold text-blue-700 mt-1':''}>{ln||<span>&nbsp;</span>}</p>)}
              </div>
            </div>
          ))}
          {busy&&<div className="flex items-start gap-2"><div className="w-7 h-7 bg-blue-900 rounded-lg flex items-center justify-center shrink-0"><BookOpen size={13} className="text-white"/></div><div className="bg-gray-100 rounded-2xl px-4 py-3 text-sm text-gray-400 animate-pulse">Searching documents...</div></div>}
          <div ref={bottom}/>
        </div>
        <div className="border-t p-4 flex gap-3">
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()} placeholder="Ask a legal question in English or Khmer..." className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500"/>
          <button onClick={send} disabled={busy||!input.trim()} className="bg-blue-900 text-white px-4 py-2.5 rounded-xl hover:bg-blue-800 disabled:opacity-50 transition"><Send size={16}/></button>
        </div>
      </div>
      {!isPaid()&&(
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
          <Lock size={18} className="text-yellow-500 shrink-0"/>
          <div className="flex-1"><p className="text-sm font-semibold text-gray-700">Free users see document references only</p><p className="text-xs text-gray-400">Subscribe to open, read and download cited documents</p></div>
          <Link to="/pricing" className="bg-yellow-500 text-blue-950 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-yellow-400 transition whitespace-nowrap">Subscribe</Link>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {['What is the land ownership law?','How to register a company?','Employee rights in Cambodia'].map(s=>(
          <button key={s} onClick={()=>setInput(s)} className="text-xs border border-gray-200 text-gray-500 px-3 py-1.5 rounded-full hover:border-blue-300 hover:text-blue-600 transition">{s}</button>
        ))}
      </div>
    </div>
  );
}
